import {
  Directive, HostListener, ElementRef,
  OnInit, Self, OnDestroy, LOCALE_ID, Inject,
  Input, ɵfindLocaleData, Optional, DEFAULT_CURRENCY_CODE
} from "@angular/core";
import { CurrencyPipe, formatNumber } from '@angular/common';
import { NgControl, ControlValueAccessor } from '@angular/forms';
import { Subscription } from 'rxjs';
import { escapeRegExp, replace } from 'lodash';
import { NumericDirective } from './numeric';
import { isNullOrUndefined } from './objectUtils';

@Directive({
    selector: "currency input[currency] input[ngModel]",
    providers: [
        CurrencyPipe,
        { provide: DEFAULT_CURRENCY_CODE, useValue: 'EUR' }
    ],
    standalone: false
})
export class CurrencyFormatterDirective implements OnInit, OnDestroy {
  private el: HTMLInputElement;
  private modelSubscription: Subscription;
  private isFocused = false;
  public parametersObject: CurrencyDirectiveParameters;

  protected digitsInfo: string;

  @Input("currency")
  public currencyCode: string = undefined;

  @Input()
  public display: 'code' | 'symbol' | 'symbol-narrow' = 'symbol';

  constructor(
    protected elementRef: ElementRef,
    protected currencyPipe: CurrencyPipe,
    @Self() protected control: NgControl,
    @Inject(LOCALE_ID) protected locale: string,
    @Self() @Optional() protected numericDirective: NumericDirective,
  ) {
    this.parametersObject = new CurrencyDirectiveParameters();
    this.el = this.elementRef.nativeElement;
    

    this.modelSubscription = this.control.valueChanges
      .subscribe(value => {
        if (!this.isFocused)
          this.el.value = this.CurrencyFormat(this.control.value);
      });
  }

  ngOnDestroy(): void {
    if (this.modelSubscription != null)
      this.modelSubscription.unsubscribe();
  }

  ngOnInit() {
    if (this.numericDirective != null)
      this.digitsInfo = "1," + this.numericDirective.parametersObject.decimalPlaces;
    else
      this.digitsInfo = "1,2";

    if (this.currencyCode != null) {
      const currencyParts = this.currencyCode.split(":");

      this.currencyCode = currencyParts.pop();
      const digitsInfoCandidate = currencyParts.pop();

      if (!digitsInfoCandidate && this.currencyCode && /\d/.test(this.currencyCode[0])) // first part starts with numeric char, means no currency code, only digits info
      {
        this.digitsInfo = this.currencyCode;
        this.currencyCode = undefined;
      }
      this.digitsInfo = digitsInfoCandidate || this.digitsInfo;
    }
    this.currencyCode = this.currencyCode || this.getLocaleCurrencyCode(this.locale);
  }

  @HostListener('keydown', ['$event']) onKeyDown(event) {
    let e = <KeyboardEvent>event;
   // if (this.OnlyNumber) {
    if ([46, 8, 9, 27, 13, 188, 189, 109, 110, 190 ].indexOf(e.keyCode) !== -1 ||
      // Allow: Ctrl+A
      (e.keyCode == 65 && e.ctrlKey === true) ||
      // Allow: Ctrl+C
      (e.keyCode == 67 && e.ctrlKey === true) ||
      // Allow: Ctrl+V
      (e.keyCode == 86 && e.ctrlKey === true) ||
      // Allow: Ctrl+X
      (e.keyCode == 88 && e.ctrlKey === true) ||
      // Allow: home, end, left, right
      (e.keyCode >= 35 && e.keyCode <= 39) ||
      // Allow: numbers
      (e.keyCode >= 48 && e.keyCode <= 57) ||
      // Allow: NumPad Numbers
      (e.keyCode >= 96 && e.keyCode <= 105)) {
      // let it happen, don't do anything

      return;
    }
    else {
      e.preventDefault();
    }
  }
  
  @HostListener('keyup', ['$event']) onKeyUp(event) {
    let e = <KeyboardEvent>event
    if ([110, 190].indexOf(e.keyCode) !== -1) {
      var position = Number(event.target.selectionStart);
      this.el.value = this.el.value.replace(/\./g, ",");
      event.target.setSelectionRange(position, position);
    }
  }
    
  @HostListener("focus")
  onFocus() {
    this.isFocused = true;
    //this.el.value = this.toEditModeFormattedNumber(this.control.value, this.currencyCode, this.display, this.digitsInfo, this.locale);
    this.parametersObject.currentValue = this.el.value;
    this.el.value = this.el.value.replace(/ /g, "");
  }

  @HostListener("blur")
  onBlur() {
    this.el.value = this.checkRegex(this.control.value, this.parametersObject);
    this.isFocused = false;
    if (this.control != undefined && this.control != null && this.control.control != undefined && this.control.control != null) {
      this.control.control.setValue(this.el.value.replace(/\,/g, ".").replace(/ /g, ""));
    }
  }

  private CurrencyFormat(inputString) {
    if (isNullOrUndefined(inputString)) {
      inputString = "";
    }
    else {
      inputString = inputString.toString();
      inputString = inputString.replace(/ /g, "");
      inputString = inputString.replace(/\./g, ",");
    }
        
    var decimalPart = "";
    if (inputString.indexOf(',') != -1) {
      inputString = inputString.split(",");
      decimalPart = "," + (inputString[1] == "" ? "00" : (inputString[1] + "00").substring(0,2));
      inputString = inputString[0];
    }
    else {
      decimalPart = inputString == "" ? "" : ",00";
    }
    var outputString = "";
    var count = 0;
    for (var i = inputString.length - 1; i >= 0 && inputString.charAt(i) != '-'; i--) {
      if (count == 3) {
        outputString += " ";
        count = 0;
      }
      outputString += inputString.charAt(i);
      count++;
    }
    if (inputString.charAt(0) == '-') {
      outputString += "-";
    }
    
    return outputString.split("").reverse().join("") + decimalPart;
  }

  private checkRegex(newValue: any, parameters: CurrencyDirectiveParameters) {
    if (isNullOrUndefined(newValue))
      this.parametersObject.currentValue = "";
    else {
      newValue = this.CurrencyFormat(newValue);

      if (RegExp(this.parametersObject.regexRule).test(newValue)) {
        this.parametersObject.currentValue = newValue;
      }
    }
    return this.parametersObject.currentValue;
  }

  /**
   * Formats a value according to currency and digits info.
   */
  protected toFullFormattedNumber(value: number | string, currencyCode: string, display: 'code' | 'symbol' | 'symbol-narrow', digitsInfo: string, locale: string): string {
    // Convert strings to numbers
    if (value == null)
      return null;

    const numberValue: number = this.toNumber(value, currencyCode, display, locale);
    return this.currencyPipe.transform(numberValue, currencyCode, display, digitsInfo, locale);
  }

  /**
   * Formats value in edit mode, only decimal separator.
   */
  protected toEditModeFormattedNumber(value: number | string, currencyCode: string, display: 'code' | 'symbol' | 'symbol-narrow', digitsInfo: string, locale: string): string {
    // Convert strings to numbers
    if (value == null)
      return null;

    const numberValue: number = this.toNumber(value, currencyCode, display, locale);
    return this.stripCurrencyAndGroupSep(formatNumber(numberValue, locale, digitsInfo), currencyCode, display, locale);
  }

  /**
   * Transforms a string into a number (if needed).
   */
  protected toNumber(value: string | number, currencyCode: string, display: 'code' | 'symbol' | 'symbol-narrow', locale: string): number {
    // Convert strings to numbers
    if (value == null)
      return null;

    if (typeof value === 'string') {
      return parseFloat(this.normalizeDecimalSep(this.stripCurrencyAndGroupSep(value, currencyCode, display, locale), locale));
    }
    if (typeof value !== 'number') {
      throw new Error(`${value} is not a number`);
    }

    return value;
  }

  private stripCurrencyAndGroupSep(value: string, currencyCode: string, display: 'code' | 'symbol' | 'symbol-narrow', locale: string): string {
    // Strip thousand separator and currency symbol.
    const extraMaskChars = this.currencyPipe.transform(1234, currencyCode, display, '1.0', locale).replace(/\d/g, '');
    const extraMaskCharsRegex = new RegExp('[' + escapeRegExp(extraMaskChars) + ']', 'g');

    return value.replace(extraMaskCharsRegex, '')
  }

  private normalizeDecimalSep(value: string, locale: string): string {
    // Set standart '.' decimal separator.
    const example = formatNumber(1.1, locale, '1.1');
    const cleanPattern = new RegExp(`[^-+0-9${example.charAt(1)}]`, 'g');

    const cleaned = value.replace(cleanPattern, '');
    return cleaned.replace(example.charAt(1), '.');
  }

  //TODO: Apagar quando suportado pelo angular, esperamos pelo PR: https://github.com/angular/angular/pull/34810
  getLocaleCurrencyCode(locale: string): string | null {
    const data = ɵfindLocaleData(locale);
    return data[15/*LocaleDataIndex.CurrencyCode*/] || null;
  }
}

class CurrencyDirectiveParameters {
  public decimalTemplate: string = "^_s_((((\\.)\\d{1,_d_})|(\\d{0,_i_}(\\.)\\d{1,_d_})|(\\d{0,_i_}(\\.))|(\\d{0,_i_})))$";
  public integerTemplate: string = "^_s_(\\d{0,_i_})$";
  public regexRule: string = "^-?(\\d+|\\d{1,3}(\\ \\d{3})+)(\\,(\\s)?\\d{2})?$";
  public integerPlaces: number = 6;
  public decimalPlaces: number = 2;
  public hasSign: boolean = false;
  public currentValue = "";
}
